import { useSyncExternalStore } from 'react';
import { restoreSession, renewSession, signOut as providerSignOut, watchRenewals } from './auth-service';
import { cachedRealm, lookupRealm, rememberRealm } from './realm';
import { lastTenant, publishSession, type Session } from './session';

/**
 * Session courante, hors React : les gardes de route TanStack (`beforeLoad`) s'exécutent avant
 * tout rendu et ne peuvent pas lire un contexte. `useSession()` n'est qu'une vue de ce store.
 */
let current: Session | null = null;
const listeners = new Set<() => void>();

function emit(): void {
  for (const listener of listeners) listener();
}

export function getSession(): Session | null {
  return current;
}

/** Abonnement aux renouvellements en arrière-plan de la session courante, et sa clé. */
let unwatch: (() => void) | null = null;
let watchedKey = '';

function watch(session: Session | null): void {
  const key = session ? `${session.tenantId}/${session.realm}` : '';
  if (key === watchedKey) return;
  unwatch?.();
  // Garde de clé : un renouvellement ne publie que s'il appartient ENCORE à l'abonnement courant,
  // quel que soit l'ordre dans lequel oidc-client-ts émet et nous nous désabonnons.
  unwatch = session
    ? watchRenewals(session.tenantId, session.realm, (renewed) => {
        if (watchedKey === key) setSession(renewed);
      })
    : null;
  watchedKey = key;
}

export function setSession(session: Session | null): void {
  if (current === session) return;
  current = session;
  publishSession(session);
  watch(session);
  emit();
}

/** Marge avant `exp` : un jeton à 2 s de sa fin passerait la garde puis prendrait un 401. */
const EXPIRY_MARGIN_SECONDS = 30;

/** `expiresAt` est en secondes (claim `exp`) ; 0 = inconnu, traité comme expiré. */
const isExpired = (session: Session): boolean =>
  (session.expiresAt - EXPIRY_MARGIN_SECONDS) * 1000 <= Date.now();

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function useSession(): Session | null {
  return useSyncExternalStore(subscribe, getSession, getSession);
}

/** Une seule restauration en vol par tenant : le layout et ses enfants la partagent. */
const inflight = new Map<string, Promise<Session | null>>();

/**
 * Résout `tenant → realm` (lookup public, mis en cache) puis tente de restaurer la session
 * stockée pour ce realm. Retourne `null` quand il faut passer par /login — c'est le seul
 * verdict sur lequel s'appuient les gardes de route.
 */
export async function ensureSession(tenantId: string): Promise<Session | null> {
  // Une session expirée n'est plus « la session courante » : sans ce test, les gardes de route
  // laissaient passer un jeton mort, et chaque écran ouvert après 5 minutes rendait des 401.
  if (current?.tenantId === tenantId && !isExpired(current)) return current;

  const pending = inflight.get(tenantId);
  if (pending) return pending;

  const promise = (async () => {
    const known = cachedRealm(tenantId) ?? (lastTenant()?.tenantId === tenantId ? lastTenant() : null);
    let realm = known?.realm;
    if (!realm) {
      try {
        realm = (await lookupRealm(tenantId)).realm;
      } catch {
        return null; // tenant inconnu (404) ou API injoignable : /login le redira proprement
      }
    } else {
      rememberRealm({ tenantId, realm, name: tenantId });
    }
    const session = (await restoreSession(tenantId, realm)) ?? (await renewSession(tenantId, realm));
    setSession(session);
    return session;
  })().finally(() => inflight.delete(tenantId));

  inflight.set(tenantId, promise);
  return promise;
}

/**
 * Déconnexion : PAR le store, pour couper l'abonnement aux renouvellements avant de quitter la
 * page. Sans cela, un renouvellement en vol republiait `Authorization` après la déconnexion.
 */
export async function signOut(tenantId: string, realm: string): Promise<void> {
  setSession(null);
  await providerSignOut(tenantId, realm);
}

/** Tests uniquement. */
export function resetAuthStore(): void {
  unwatch?.();
  unwatch = null;
  watchedKey = '';
  current = null;
  inflight.clear();
  listeners.clear();
}
